// AddCategory.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button, Form, Dropdown } from 'react-bootstrap';
import { X, Eye, EyeSlash } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { addMenuCategoryToMenu } from '../../../../../../../../../../crud/menu_api/menus';
import { getAllParentsCategoriesByLocalId } from '../../../../../../../../../../crud/menu_api/categories';
import MiniLoadingSpinner from '../../../../../../../../../../components/Spinners/MiniLoadingSpinner';
import './AddCategory.scss';

export interface ParentCategoryOption {
  id: number;
  name: string;
  isVisible: boolean;
}

interface AddCategoryProps {
  show: boolean;
  handleClose: () => void;
  menu: any;
  setSelectedMenu: (menu: any) => void;
  menus: any[];
  setMenus: (menus: any[]) => void;
}

const AddCategory: React.FC<AddCategoryProps> = ({
  show,
  handleClose,
  menu,
  setSelectedMenu,
  menus,
  setMenus,
}) => {
  const [categories, setCategories] = useState<ParentCategoryOption[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const selectedCategory = categories.find(c => c.id === selectedId) || null;

  const fetchOptions = useCallback(async () => {
    setLoading(true);
    try {
      const userContext = localStorage.getItem('user');
      const localId = userContext ? JSON.parse(userContext).local_id : 0;
      const resp = await getAllParentsCategoriesByLocalId(localId);
      
      if (resp?.data) {
        const existingIds = new Set(menu.categories?.map((cat:any) => cat.id) || []);
        const opts: ParentCategoryOption[] = resp.data
          .filter((cat: any) => !existingIds.has(cat.category_id))
          .map((cat: any) => ({
            id: cat.category_id,
            name: cat.category_name,
            isVisible: cat.is_main || false, // is_main indicates if the category is visible to clients
          }));
        setCategories(opts);
      }
    } catch (err) {
      console.error('Error fetching parent categories:', err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [menu.categories]);

  useEffect(() => {
    if (show) {
      setSelectedId(null);
      fetchOptions();
    }
  }, [show, fetchOptions]);

  const handleSelect = (key: string | null) => {
    setSelectedId(key ? Number(key) : null);
  };

  const handleAdd = async () => {
    if (!selectedCategory) {
      toast.warning('Por favor selecciona una categoría.');
      return;
    }
    try {
      await addMenuCategoryToMenu(menu.menu_id, selectedCategory.id);
      toast.success('Categoría agregada al menú con éxito!');

      const updatedCats = [
        ...(menu.categories || []),
        { id: selectedCategory.id, name: selectedCategory.name },
      ];
      setSelectedMenu({ ...menu, categories: updatedCats });
      setMenus(
        menus.map(m =>
          m.menu_id === menu.menu_id
            ? { ...m, categories: updatedCats }
            : m
        )
      );

      handleClose();
    } catch (err) {
      console.error('Error al agregar la categoría al menú:', err);
      toast.error('Error al agregar la categoría al menú');
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="add-category-to-menu-modal">
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            Agregar Categoría al Menú
          </Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={handleClose}>
          <X size={24} />
        </Button>
      </Modal.Header>

      {loading && (
        <div className="loading-overlay">
          <MiniLoadingSpinner />
        </div>
      )}
      
      {!loading && (
        <Modal.Body>
          <div className="form-container">
            <p>
              Selecciona una categoría para agregar al menú{' '}
              <strong>{menu.menu_name}</strong>:
            </p>

            {/* Legend explaining the icons */}
            <div className="visibility-legend">
              <h6>Leyenda de visibilidad:</h6>
              <div className="legend-items">
                <div className="legend-item">
                  <Eye className="legend-icon visible" />
                  <span>Visible para clientes</span>
                </div>
                <div className="legend-item">
                  <EyeSlash className="legend-icon hidden" />
                  <span>Oculta para clientes</span>
                </div>
              </div>
            </div>

            <Form.Group className="mp-formgroup horizontal">
              <Form.Label>Categoría</Form.Label>
              <Dropdown onSelect={handleSelect}>
                <Dropdown.Toggle
                  variant="light"
                  id="dropdown-add-category"
                  className="dropdown-toggle"
                >
                  {selectedCategory ? (
                    <div className="selected-category-display">
                      {selectedCategory.isVisible ? (
                        <Eye className="category-icon visible" />
                      ) : (
                        <EyeSlash className="category-icon hidden" />
                      )}
                      <span>{selectedCategory.name}</span>
                    </div>
                  ) : (
                    'Seleccionar categoría'
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {categories.length === 0 ? (
                    <Dropdown.Item disabled>
                      No hay categorías disponibles
                    </Dropdown.Item>
                  ) : (
                    categories.map(cat => (
                      <Dropdown.Item
                        key={cat.id}
                        eventKey={cat.id.toString()}
                        className="category-dropdown-item"
                      >
                        <div className="category-item-content">
                          {cat.isVisible ? (
                            <Eye className="category-icon visible" />
                          ) : (
                            <EyeSlash className="category-icon hidden" />
                          )}
                          <span className="category-name">{cat.name}</span>
                        </div>
                      </Dropdown.Item>
                    ))
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
          </div>
        </Modal.Body>
      )}

      <Modal.Footer>
        <Button
          variant="none"
          onClick={handleClose}
          className="form-button-cancel"
        >
          Cancelar
        </Button>
        <Button
          variant="none"
          onClick={handleAdd}
          className="form-button-create"
          disabled={!selectedCategory}
        >
          Agregar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddCategory;
